import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const CACHE_KEY = 'conectate:homepage:v3';
const CACHE_TTL_MS = 10 * 60 * 1000;

const EMPTY_DATA = {
  videos: [],
  faqs: [],
  speakers: [],
  scheduleDays: [],
  schedule: [],
  participatingCountries: [],
  galleryImages: [],
  aboutImages: [],
  formFields: [],
  content: {
    hero: {
      title: 'Conéctate CCI',
      subtitle: 'Un encuentro internacional para conectar, equipar y movilizar líderes.',
    },
  },
};

const normalizeHomepageData = (data = {}) => {
  const contentMap = {};
  (data.content_sections || []).forEach((section) => {
    contentMap[section.section_name] = section.content;
  });

  return {
    videos: data.videos || [],
    faqs: data.faqs || [],
    speakers: data.speakers || [],
    scheduleDays: data.schedule_days || [],
    schedule: data.schedule_events || [],
    participatingCountries: data.participating_countries || [],
    galleryImages: data.gallery_images || [],
    aboutImages: data.about_images || [],
    formFields: data.form_fields || [],
    content: {
      ...EMPTY_DATA.content,
      ...contentMap,
    },
  };
};

const readCache = () => {
  try {
    const cached = JSON.parse(sessionStorage.getItem(CACHE_KEY));
    if (!cached?.savedAt || Date.now() - cached.savedAt > CACHE_TTL_MS) return null;
    return cached.data;
  } catch {
    return null;
  }
};

const writeCache = (data) => {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ savedAt: Date.now(), data }));
  } catch {
    // La navegación sigue funcionando aunque el navegador bloquee sessionStorage.
  }
};

export default function useHomepageData() {
  const [initialCache] = useState(readCache);
  const [data, setData] = useState(initialCache || EMPTY_DATA);
  const [loading, setLoading] = useState(!initialCache);
  const [loadError, setLoadError] = useState(false);

  const refresh = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setLoadError(false);

    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const { data: response, error } = await supabase.rpc('get_homepage_data');
        if (error) throw error;
        if (!response) throw new Error('Respuesta vacía de get_homepage_data');

        const normalized = normalizeHomepageData(response);
        setData(normalized);
        writeCache(normalized);
        setLoading(false);
        return;
      } catch (error) {
        if (attempt === 0) {
          await new Promise((resolve) => setTimeout(resolve, 800));
          continue;
        }
        console.error('No fue posible actualizar la portada:', error);
        setLoadError(true);
      }
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    refresh({ silent: Boolean(initialCache) });
  }, [initialCache, refresh]);

  return {
    ...data,
    loading,
    loadError,
    refresh,
  };
}
